import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import useTemporaryInlineStyles from 'src/js/modules/breakpoint-editing/hooks/use-temporary-inline-styles';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import computeCornersFallback from '../helpers/compute-corners-fallback';

const { Paragraph, Icon } = designSystem;
const {
  components: { SliderControl },
  constants: { targetClasses },
} = elementSettings;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;

const TARGET_TO_TEMPORARY_STYLES = {
  [targetClasses.ROUNDED_TOP_LEFT]: (value) => ({
    'border-top-left-radius': value,
  }),
  [targetClasses.ROUNDED_TOP_RIGHT]: (value) => ({
    'border-top-right-radius': value,
  }),
  [targetClasses.ROUNDED_BOTTOM_LEFT]: (value) => ({
    'border-bottom-left-radius': value,
  }),
  [targetClasses.ROUNDED_BOTTOM_RIGHT]: (value) => ({
    'border-bottom-right-radius': value,
  }),
};

const CORNERS = [
  {
    id: 'top-left-corner',
    target: targetClasses.ROUNDED_TOP_LEFT,
    iconGlyph: 'corner-top-left-radius',
  },
  {
    id: 'top-right-corner',
    target: targetClasses.ROUNDED_TOP_RIGHT,
    iconGlyph: 'corner-top-right-radius',
  },
  {
    id: 'bottom-left-corner',
    target: targetClasses.ROUNDED_BOTTOM_LEFT,
    iconGlyph: 'corner-bottom-left-radius',
  },
  {
    id: 'bottom-right-corner',
    target: targetClasses.ROUNDED_BOTTOM_RIGHT,
    iconGlyph: 'corner-bottom-right-radius',
  },
];

const parseValueWithUnit = (value) => `${parseFloat(value)}px`;

const getLiveElements = (elementIds) =>
  elementIds.map((elementId) => view.accessors.getLiveElement(elementId)).filter(Boolean);

const getPopoverProps = ({ imgSrc, title, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

function CornersControl({ elementIds, targets, onChange }) {
  const { setTemporaryStyles, restoreTemporaryStyles } = useTemporaryInlineStyles();

  const items = useMemo(
    () =>
      CORNERS.map(({ id, target, iconGlyph }) => ({
        id,
        targets: [target],
        iconGlyph,
      })),
    []
  );

  const handleChange = useCallback(
    ({ controlId, elementIds: ids, targets: cornerTargets, value, skipTracking }) => {
      const parsedValue = parseValueWithUnit(value);

      setTemporaryStyles({
        elements: getLiveElements(ids),
        styles: Object.assign(
          {},
          ...cornerTargets.map((target) => TARGET_TO_TEMPORARY_STYLES[target](parsedValue))
        ),
      });

      onChange({
        controlId,
        elementIds: ids,
        targets: cornerTargets,
        value: parsedValue,
        skipTracking,
        skipApplyChanges: true,
        shouldScrollToTarget: false,
      });
    },
    [onChange, setTemporaryStyles]
  );

  const handleAfterChange = useCallback(
    ({ controlId, elementIds: ids, targets: cornerTargets, value }) => {
      restoreTemporaryStyles({ elements: getLiveElements(ids) });

      onChange({
        controlId,
        elementIds: ids,
        targets: cornerTargets,
        value: parseValueWithUnit(value),
      });
    },
    [onChange, restoreTemporaryStyles]
  );

  return (
    <SliderControl
      id={controlIds.CORNERS}
      elementIds={elementIds}
      targets={targets}
      title={i18next.t('Independent corners')}
      iconGlyph="corner-radius"
      mixedLabel={i18next.t('Mixed')}
      step={SLIDER_STEP}
      min={SLIDER_MIN}
      max={SLIDER_MAX}
      items={items}
      popover={getPopoverProps({
        imgSrc: previewGifs.COLUMN_CORNERS_GIF,
        title: i18next.t('Adjust corners for columns'),
        iconGlyph: 'corner-radius',
      })}
      computeFallbackValue={computeCornersFallback}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
    />
  );
}

CornersControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CornersControl;
